import { CountryItem } from '@/models/dictionary/country-list.model';
import { ColumnFilterAlignments, ColumnFilterItem } from '@/models/column-filter/column-filter.model';
import { GeoPaymentMethodDepositLimits } from '@/models/ps-management/limits.model';
import { SegmentItem } from '@/models/dictionary/segments.model';
import { PaymentTypeItem } from '@/models/dictionary/payment-type-list.model';
import { TagListItem, TagListItemResponseModel } from '@/models/dictionary/tag-list.model';

// ---------------------------
// SHARED / COMMON TYPES
// ---------------------------

export type PaymentMethodCredentialsEntry = {
  secret: string;
  twoFactorCode: string;
};

export type LimitListItem = {
  currency: string;
  min: number;
  max: number;
};

export type GeoLimitListItem = LimitListItem & {
  extendedLimits?: number;
};

export const DEFAULT_CURRENCY_ITEM: LimitListItem = {
  min: 0,
  max: 0,
  currency: '',
};

export type PaymentMethodBase = {
  billingProviderId: string;
  billingPaymentMethodId: string;
  billingPaymentMethodSettingsId?: string;
  psp: string;
  paymentSystem: string;
  publicName: string;
  imageId: string | null;
  filePath: string | null;
  depositEnabled: boolean;
  withdrawEnabled: boolean;
  segmentList: SegmentItem[];
  excludedSegmentList: SegmentItem[];
  depositLimitList: LimitListItem[];
  withdrawLimitList: LimitListItem[];
  paymentTypes: PaymentTypeItem[];
  underExperiment?: boolean;
};

// ---------------------------
// GLOBAL PAYMENT MODEL
// ---------------------------

export type GlobalPaymentMethodItem = PaymentMethodBase & {
  billingPaymentMethodSettingsId: string;
  vaultEnabled: boolean;
  billingProviderName: string;
  autoWithdrawAvailable: boolean;
  secretsAdded: boolean | null;
  secretsFormat: string | null;
  changeDepositStatusAvailable: boolean;
  changeWithdrawStatusAvailable: boolean;
  underExperiment: boolean;
  isCurrencyConversionAvailable: boolean;
  currencyConverterList: Record<string, string[]>;
};

export type CreateGlobalPaymentMethodData = Omit<
GlobalPaymentMethodItem,
| 'billingPaymentMethodId'
| 'billingPaymentMethodSettingsId'
| 'vaultEnabled'
| 'billingProviderName'
| 'filePath'
| 'secretsAdded'
| 'secretsFormat'
>;

export type UpdateGlobalPaymentMethodData = Omit<
GlobalPaymentMethodItem,
| 'billingPaymentMethodId'
| 'depositEnabled'
| 'withdrawEnabled'
| 'billingProviderName'
| 'filePath'
| 'secretsAdded'
| 'secretsFormat'
>;

export type GlobalPaymentMethodFormData = Partial<
Omit<
GlobalPaymentMethodItem,
'billingProviderName' | 'filePath' | 'secretsAdded' | 'secretsFormat' | 'depositLimitList' | 'withdrawLimitList'
>
> & {
  depositEnabled?: boolean;
  withdrawEnabled?: boolean;
  depositLimitList: GlobalPaymentMethodItem['depositLimitList'];
  withdrawLimitList: GlobalPaymentMethodItem['withdrawLimitList'];
};

export const DEFAULT_GLOBAL_PAYMENT_METHOD_FORM: GlobalPaymentMethodFormData = {
  billingProviderId: '',
  psp: '',
  paymentSystem: '',
  imageId: '',
  depositEnabled: false,
  withdrawEnabled: false,
  segmentList: [],
  excludedSegmentList: [],
  depositLimitList: [],
  withdrawLimitList: [],
  paymentTypes: [],
  autoWithdrawAvailable: false,
  changeDepositStatusAvailable: false,
  changeWithdrawStatusAvailable: false,
};

export type GlobalPaymentMethodList = GlobalPaymentMethodItem[];

export type GetGlobalPaymentMethodListRequestPayload = {
  limit: number;
  offset: number;
};

export type GlobalPaymentMethodPartialUpdatePayload = {
  billingPaymentMethodId: string;
  data: {
    depositEnabled?: boolean;
    withdrawEnabled?: boolean;
  };
};

export type GlobalPaymentMethodPartialUpdateParameterKeyModel =
  | GlobalPaymentMethodColumnProperties.WITHDRAW_ENABLED
  | GlobalPaymentMethodColumnProperties.DEPOSIT_ENABLED;

export type GlobalPaymentMethodPartialUpdateParams = {
  parameterKey: GlobalPaymentMethodPartialUpdateParameterKeyModel;
  billingPaymentMethodId: GlobalPaymentMethodItem['billingPaymentMethodId'];
  newValue: boolean;
};

// ---------------------------
// GEO PAYMENT MODEL
// ---------------------------

export enum GeoPaymentMethodTypes {
  SINGLE = 'single',
  MULTI = 'multi',
}

export enum GeoPaymentMethodOperation {
  DEPOSIT = 'deposit',
  WITHDRAW = 'withdraw',
  ALL = 'all',
}

export type GeoPaymentMethodItem = PaymentMethodBase & {
  type: GeoPaymentMethodTypes.SINGLE;
  sortingWeight: number;
  depositLimits?: GeoPaymentMethodDepositLimits | GeoLimitListItem[];
  currencyList?: string[];
  billingPaymentMethodSettingsId: string;
  billingProviderName: string;
  country: string;
  imageUrl: string;
  operation: string;
  organicsEnabled: boolean;
  earlyDeposit?: boolean;
  tagList?: TagListItem[] | TagListItemResponseModel[];
  excludedTagList?: TagListItem[] | TagListItemResponseModel[];
  visible?: boolean;
  underExperiment?: boolean;
  secretsFormat: string | null;
  secretsAdded: boolean | null;
  availableEarlyDepositForPaymentMethod: boolean;
  isUnavailableHoursActive: boolean;
  isCPFCheckerActive?: boolean;
};

export type GeoMultiPaymentMethodListItem = {
  type: GeoPaymentMethodTypes.MULTI;
  multiMethodId: string;
  publicName: string;
  imageId: string | null;
  filePath: string | null;
  withdrawalAvailable: boolean;
  currency: string;
  enabled: boolean;
  organic: boolean;
  sortingWeight: number;
  segmentList: SegmentItem[];
  excludedSegmentList: SegmentItem[];
  depositRange: { min: number; max: number };
  withdrawalRange: { min: number; max: number };
  underExperiment?: boolean;
};

export type GeoSinglePaymentMethodListItem = GeoPaymentMethodItem;

export type GeoPaymentMethodList = Array<GeoSinglePaymentMethodListItem | GeoMultiPaymentMethodListItem>;

export type GeoPaymentMethodWithIdList = Array<
(GeoMultiPaymentMethodListItem | GeoSinglePaymentMethodListItem) & { id: string }
>;

export const DEFAULT_GEO_PAYMENT_METHOD_FORM: GeoPaymentMethodItem = {
  type: GeoPaymentMethodTypes.SINGLE,
  billingProviderId: '',
  billingPaymentMethodId: '',
  billingProviderName: '',
  sortingWeight: 0,
  publicName: '',
  billingPaymentMethodSettingsId: '',
  psp: '',
  paymentSystem: '',
  imageId: '',
  filePath: '',
  imageUrl: '',
  operation: '',
  depositEnabled: false,
  withdrawEnabled: false,
  segmentList: [],
  excludedSegmentList: [],
  depositLimitList: [],
  withdrawLimitList: [],
  country: 'EN',
  paymentTypes: [],
  secretsFormat: null,
  secretsAdded: null,
  organicsEnabled: false,
  visible: true,
  underExperiment: false,
  availableEarlyDepositForPaymentMethod: false,
  isUnavailableHoursActive: false,
};

export type CreateGeoSingleMethodRequestPayload = {
  countryCode: string;
  payloadData: { billingPaymentMethodIdList: string[] };
};

export type GetGeoPaymentMethodListRequestPayload = {
  countryCode: string;
  params: {
    limit: number;
    offset: number;
    filterList: {
      operation: GeoPaymentMethodOperation;
    };
  };
};

export type GeoPaymentMethodUpdateRequestPayload = {
  billingPaymentMethodSettingsId: string;
  paymentMethodData: GeoPaymentMethodItem;
};

export type GeoPaymentMethodPartialUpdatePayload = {
  billingPaymentMethodSettingsId: string;
  data: {
    depositEnabled?: boolean;
    withdrawEnabled?: boolean;
  };
};

export type GeoSinglePaymentMethodPartialUpdateParameterKeyModel = GeoPaymentMethodColumnProperties.WITHDRAW_ENABLED |
GeoPaymentMethodColumnProperties.DEPOSIT_ENABLED;

export type GeoSinglePaymentMethodPartialUpdateParams = {
  parameterKey: GeoSinglePaymentMethodPartialUpdateParameterKeyModel;
  billingPaymentMethodSettingsId: GeoPaymentMethodItem['billingPaymentMethodSettingsId'];
  newValue: boolean;
}

export type UpdateListSortGeoPaymentMethodRequestPayload = {
  countryCode: CountryItem['code'];
  payloadData: {
    paymentMethodSortingList: Array<{ methodId: string; sortingWeight: number }>;
  };
};

export const geoPaymentMethodOperationOptions = [
  { label: 'Deposit', value: GeoPaymentMethodOperation.DEPOSIT },
  { label: 'Withdrawal', value: GeoPaymentMethodOperation.WITHDRAW },
  { label: 'All', value: null },
];

export enum GlobalPaymentMethodColumnProperties {
  IMAGE_URL = 'imageUrl',
  PAYMENT_SYSTEM = 'paymentSystem',
  BILLING_PROVIDER_NAME = 'billingProviderName',
  PAYMENT_TYPE_NAME = 'paymentTypes',
  SEGMENT_LIST = 'segmentList',
  EXCLUDED_SEGMENT_LIST = 'excludedSegmentList',
  DEPOSIT_LIMIT_LIST = 'depositLimitList',
  CURRENCY_CONVERTER_LIST = 'currencyConverterList',
  WITHDRAW_LIMIT_LIST = 'withdrawLimitList',
  DEPOSIT_ENABLED = 'depositEnabled',
  WITHDRAW_ENABLED = 'withdrawEnabled',
  ACTIONS = 'actions',
}

export enum GlobalPaymentMethodColumnLabels {
  IMAGE_URL = 'Logo',
  BRAND = 'Brand',
  PAYMENT_SYSTEM = 'Name',
  BILLING_PROVIDER_NAME = 'Billing provider',
  PAYMENT_TYPE_NAME = 'Payment type',
  SEGMENT_LIST = 'Included segments',
  EXCLUDED_SEGMENT_LIST = 'Excluded segments',
  DEPOSIT_LIMIT_LIST = 'Deposit limit',
  CURRENCY_CONVERTER_LIST = 'Converter',
  WITHDRAW_LIMIT_LIST = 'Withdrawal limit',
  DEPOSIT_ENABLED = 'Deposit',
  WITHDRAW_ENABLED = 'Withdrawal',
  ACTIONS = 'Actions',
}

export const globalPaymentMethodColumnLabels = {
  [GlobalPaymentMethodColumnProperties.IMAGE_URL]: GlobalPaymentMethodColumnLabels.IMAGE_URL,
  [GlobalPaymentMethodColumnProperties.PAYMENT_SYSTEM]: GlobalPaymentMethodColumnLabels.PAYMENT_SYSTEM,
  [GlobalPaymentMethodColumnProperties.BILLING_PROVIDER_NAME]: GlobalPaymentMethodColumnLabels.BILLING_PROVIDER_NAME,
  [GlobalPaymentMethodColumnProperties.PAYMENT_TYPE_NAME]: GlobalPaymentMethodColumnLabels.PAYMENT_TYPE_NAME,
  [GlobalPaymentMethodColumnProperties.SEGMENT_LIST]: GlobalPaymentMethodColumnLabels.SEGMENT_LIST,
  [GlobalPaymentMethodColumnProperties.EXCLUDED_SEGMENT_LIST]: GlobalPaymentMethodColumnLabels.EXCLUDED_SEGMENT_LIST,
  [GlobalPaymentMethodColumnProperties.DEPOSIT_LIMIT_LIST]: GlobalPaymentMethodColumnLabels.DEPOSIT_LIMIT_LIST,
  [GlobalPaymentMethodColumnProperties.CURRENCY_CONVERTER_LIST]:
    GlobalPaymentMethodColumnLabels.CURRENCY_CONVERTER_LIST,
  [GlobalPaymentMethodColumnProperties.WITHDRAW_LIMIT_LIST]: GlobalPaymentMethodColumnLabels.WITHDRAW_LIMIT_LIST,
  [GlobalPaymentMethodColumnProperties.DEPOSIT_ENABLED]: GlobalPaymentMethodColumnLabels.DEPOSIT_ENABLED,
  [GlobalPaymentMethodColumnProperties.WITHDRAW_ENABLED]: GlobalPaymentMethodColumnLabels.WITHDRAW_ENABLED,
  [GlobalPaymentMethodColumnProperties.ACTIONS]: GlobalPaymentMethodColumnLabels.ACTIONS,
};

export const globalPaymentMethodColumnsList: ColumnFilterItem[] = [
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.IMAGE_URL],
    value: GlobalPaymentMethodColumnProperties.IMAGE_URL,
    align: ColumnFilterAlignments.CENTER,
    width: 120,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.PAYMENT_SYSTEM],
    value: GlobalPaymentMethodColumnProperties.PAYMENT_SYSTEM,
    width: 130,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.BILLING_PROVIDER_NAME],
    value: GlobalPaymentMethodColumnProperties.BILLING_PROVIDER_NAME,
    width: 130,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.PAYMENT_TYPE_NAME],
    value: GlobalPaymentMethodColumnProperties.PAYMENT_TYPE_NAME,
    width: 215,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.SEGMENT_LIST],
    value: GlobalPaymentMethodColumnProperties.SEGMENT_LIST,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.EXCLUDED_SEGMENT_LIST],
    value: GlobalPaymentMethodColumnProperties.EXCLUDED_SEGMENT_LIST,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.DEPOSIT_LIMIT_LIST],
    value: GlobalPaymentMethodColumnProperties.DEPOSIT_LIMIT_LIST,
    width: 140,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.CURRENCY_CONVERTER_LIST],
    value: GlobalPaymentMethodColumnProperties.CURRENCY_CONVERTER_LIST,
    width: 160,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.WITHDRAW_LIMIT_LIST],
    value: GlobalPaymentMethodColumnProperties.WITHDRAW_LIMIT_LIST,
    width: 140,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.DEPOSIT_ENABLED],
    value: GlobalPaymentMethodColumnProperties.DEPOSIT_ENABLED,
    align: ColumnFilterAlignments.CENTER,
    width: 150,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.WITHDRAW_ENABLED],
    value: GlobalPaymentMethodColumnProperties.WITHDRAW_ENABLED,
    align: ColumnFilterAlignments.CENTER,
    width: 150,
  },
  {
    label: globalPaymentMethodColumnLabels[GlobalPaymentMethodColumnProperties.ACTIONS],
    value: GlobalPaymentMethodColumnProperties.ACTIONS,
    align: ColumnFilterAlignments.CENTER,
    width: 70,
  },
];

export enum GeoPaymentMethodColumnProperties {
  IMAGE_URL = 'imageUrl',
  PAYMENT_SYSTEM = 'paymentSystem',
  PUBLIC_NAME = 'publicName',
  BILLING_PROVIDER_NAME = 'billingProviderName',
  SEGMENT_LIST = 'segmentList',
  EXCLUDED_SEGMENT_LIST = 'excludedSegmentList',
  TAG_LIST = 'tagList',
  EXCLUDED_TAG_LIST = 'excludedTagList',
  DEPOSIT_LIMIT_LIST = 'depositLimitList',
  WITHDRAW_LIMIT_LIST = 'withdrawLimitList',
  ORGANICS_ENABLED = 'organicsEnabled',
  DEPOSIT_ENABLED = 'depositEnabled',
  WITHDRAW_ENABLED = 'withdrawEnabled',
  ACTIONS = 'actions',
  CURRENCY = 'currency',
  TYPE = 'type',
}

export enum GeoPaymentMethodColumnLabels {
  IMAGE_URL = 'Logo',
  BRAND = 'Brand',
  PAYMENT_SYSTEM = 'Internal name',
  PUBLIC_NAME = 'Public name',
  BILLING_PROVIDER_NAME = 'Billing provider',
  SEGMENT_LIST = 'Included segments',
  EXCLUDED_SEGMENT_LIST = 'Excluded segments',
  TAG_LIST = 'Included tags',
  EXCLUDED_TAG_LIST = 'Excluded tags',
  DEPOSIT_LIMIT_LIST = 'Deposit limit',
  WITHDRAW_LIMIT_LIST = 'Withdrawal limit',
  DEPOSIT_ENABLED = 'Deposit',
  WITHDRAW_ENABLED = 'Withdrawal',
  ACTIONS = 'Actions',
  CURRENCY = 'Currency',
  TYPE = 'Method',
}

export const geoPaymentMethodColumnLabels = {
  [GeoPaymentMethodColumnProperties.IMAGE_URL]: GeoPaymentMethodColumnLabels.IMAGE_URL,
  [GeoPaymentMethodColumnProperties.PAYMENT_SYSTEM]: GeoPaymentMethodColumnLabels.PAYMENT_SYSTEM,
  [GeoPaymentMethodColumnProperties.PUBLIC_NAME]: GeoPaymentMethodColumnLabels.PUBLIC_NAME,
  [GeoPaymentMethodColumnProperties.BILLING_PROVIDER_NAME]: GeoPaymentMethodColumnLabels.BILLING_PROVIDER_NAME,
  [GeoPaymentMethodColumnProperties.SEGMENT_LIST]: GeoPaymentMethodColumnLabels.SEGMENT_LIST,
  [GeoPaymentMethodColumnProperties.EXCLUDED_SEGMENT_LIST]: GeoPaymentMethodColumnLabels.EXCLUDED_SEGMENT_LIST,
  [GeoPaymentMethodColumnProperties.TAG_LIST]: GeoPaymentMethodColumnLabels.TAG_LIST,
  [GeoPaymentMethodColumnProperties.EXCLUDED_TAG_LIST]: GeoPaymentMethodColumnLabels.EXCLUDED_TAG_LIST,
  [GeoPaymentMethodColumnProperties.DEPOSIT_LIMIT_LIST]: GeoPaymentMethodColumnLabels.DEPOSIT_LIMIT_LIST,
  [GeoPaymentMethodColumnProperties.WITHDRAW_LIMIT_LIST]: GeoPaymentMethodColumnLabels.WITHDRAW_LIMIT_LIST,
  [GeoPaymentMethodColumnProperties.DEPOSIT_ENABLED]: GeoPaymentMethodColumnLabels.DEPOSIT_ENABLED,
  [GeoPaymentMethodColumnProperties.WITHDRAW_ENABLED]: GeoPaymentMethodColumnLabels.WITHDRAW_ENABLED,
  [GeoPaymentMethodColumnProperties.ACTIONS]: GeoPaymentMethodColumnLabels.ACTIONS,
  [GeoPaymentMethodColumnProperties.CURRENCY]: GeoPaymentMethodColumnLabels.CURRENCY,
  [GeoPaymentMethodColumnProperties.TYPE]: GeoPaymentMethodColumnLabels.TYPE,
};

export const geoPaymentMethodColumnsList: ColumnFilterItem[] = [
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.IMAGE_URL],
    value: GeoPaymentMethodColumnProperties.IMAGE_URL,
    align: ColumnFilterAlignments.CENTER,
    width: 120,
  },
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.PAYMENT_SYSTEM],
    value: GeoPaymentMethodColumnProperties.PAYMENT_SYSTEM,
    width: 130,
  },
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.CURRENCY],
    value: GeoPaymentMethodColumnProperties.CURRENCY,
    width: 130,
    isShowOverflowTooltip: true,
  },
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.TYPE],
    value: GeoPaymentMethodColumnProperties.TYPE,
    width: 80,
  },
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.PUBLIC_NAME],
    value: GeoPaymentMethodColumnProperties.PUBLIC_NAME,
    width: 130,
  },
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.BILLING_PROVIDER_NAME],
    value: GeoPaymentMethodColumnProperties.BILLING_PROVIDER_NAME,
    width: 130,
  },
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.SEGMENT_LIST],
    value: GeoPaymentMethodColumnProperties.SEGMENT_LIST,
  },
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.EXCLUDED_SEGMENT_LIST],
    value: GeoPaymentMethodColumnProperties.EXCLUDED_SEGMENT_LIST,
  },
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.TAG_LIST],
    value: GeoPaymentMethodColumnProperties.TAG_LIST,
  },
  {
    label: geoPaymentMethodColumnLabels[GeoPaymentMethodColumnProperties.EXCLUDED_TAG_LIST],
    value: GeoPaymentMethodColumnProperties.EXCLUDED_TAG_LIST,
  },
];

export enum GlobalDropdownActionCommand {
  OPEN_EDIT_PAYMENT_METHOD = 'OPEN_EDIT_PAYMENT_METHOD',
  OPEN_ADD_SECRET_KEY = 'OPEN_ADD_SECRET_KEY',
  OPEN_EXCEPTION_LIMIT_SETTINGS = 'OPEN_EXCEPTION_LIMIT_SETTINGS',
  OPEN_INTERNAL_CONVERTER_SETTINGS = 'OPEN_INTERNAL_CONVERTER_SETTINGS',
}

export enum GeoDropdownActionCommand {
  OPEN_EDIT_PAYMENT_METHOD = 'OPEN_EDIT_PAYMENT_METHOD',
  OPEN_PSP_LIMITS_SETTINGS = 'OPEN_PSP_LIMITS_SETTINGS',
  OPEN_EXCEPTION_LIMIT_SETTINGS = 'OPEN_EXCEPTION_LIMIT_SETTINGS',
  OPEN_UNAVAILABLE_HOURS_SETTINGS = 'OPEN_UNAVAILABLE_HOURS_SETTINGS',
}

export enum OrganicsAvailabilityStatus {
  AVAILABLE = 'Available',
  UNAVAILABLE = 'Unavailable',
}

export enum OrganicsAvailabilityTextType {
  SUCCESS = 'success',
  DANGER = 'danger',
}

export type OrganicsAvailabilityData = {
  type: OrganicsAvailabilityTextType;
  value: OrganicsAvailabilityStatus;
}

export const CHANGE_DEPOSIT_STATUS_AVAILABLE_TOOLTIP_CONTENT =
  'Allows enabling final status changes (Paid/Cancelled) for deposit transactions';

export const CHANGE_WITHDRAWAL_STATUS_AVAILABLE_TOOLTIP_CONTENT =
  'Allows enabling final status changes (Paid/Cancelled) for withdrawal transactions';

export enum GlobalPaymentMethodSidebarTitle {
  CREATE = 'Create payment method',
  EDIT = 'Edit payment method',
}
